// The document editor (spec/dashboard.md section 6.2): a slide-over from the right with the root document in the URL
// (`?doc=<id>&tab=`), the fields grouped by the declaring template, the right column (export, tags, notes, staged changes,
// actions), the References / History / JSON tabs. One stack, no nested dialogs (section 6.4): an open from inside the
// editor (the picker's "+ Create", a reference's Open) pushes an entry shown with a breadcrumb; Done pops to the parent
// (the picker then commits the reference), Cancel reverts the entry's edits since its mark and pops (section 4.4), a crumb
// click pops to that entry confirming the ones above. Escape, the close button and the backdrop are Cancel, and ask
// first when the entry changed something since it opened. Done and save all (Ctrl+Enter) is Done then the shell's Save All.
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { ApiError } from '../api/client';
import { ops } from '../api/operations';
import type { Commit, Document, DocumentReference, Json, Parameter } from '../api/types';
import { useGame } from '../shell/GameContext';
import { routes } from '../shell/routes';
import { Button, IconButton } from '../ui/Button';
import { ChevronDown, Undo2 } from '../ui/icons';
import { Badge, draftTone, draftWord } from '../ui/Chip';
import { useConfirm } from '../ui/ConfirmDialog';
import { Field } from '../ui/Field';
import { CommitTextInput, Switch } from '../ui/inputs';
import { Modal, useUrlParam } from '../ui/Modal';
import { Menu } from '../ui/Popover';
import { Tabs } from '../ui/Tabs';
import { formatInstant, useTimeZone } from '../ui/time';
import { useToasts } from '../ui/Toast';
import { TagInput } from '../ui/values';
import { useQuery } from '../workspace/cache';
import { useWorkspace, useWorkspaceVersion } from '../workspace/react';
import { entityKey, isTempId } from '../workspace/Workspace';
import { baseOf, cloneDocument } from './actions';
import { stageDeletion } from './DocumentActions';
import { ChangeTemplateDialog } from './dialogs/ChangeTemplateDialog';
import { CopyToBranchDialog } from './dialogs/CopyToBranchDialog';
import { ReplaceByDialog } from './dialogs/ReplaceByDialog';
import { useContent } from './ContentContext';
import { shortId } from './format';
import { parameterHelp } from './help';
import { useCreateFor } from './overrides/useCreateFor';
import { FieldWidget } from './widgets/FieldWidget';
import { EditorHostContext, joinPath, useWidgetEnv, type EditorHost, type EditorOrigin, type EditorStackEntry } from './widgets/context';

type Outcome = 'confirmed' | 'cancelled';
/** how an entry closes: Done, Cancel (reverts to its mark), Delete (pops without reverting or committing) */
export type CloseOutcome = Outcome | 'deleted';

interface Entry extends EditorStackEntry {
  /** Cancel reverts to this mark */
  cancelMark: number;
  /** the undo position when the entry opened: past it, Escape and the backdrop ask first */
  openMark: number;
  resolve?: (outcome: Outcome) => void;
}

export interface EditorCrumb {
  label: string;
  /** the field of the parent the next entry was created from ("Store item") */
  via?: string;
  onSelect: () => void;
}

/**
 * Hosts the slide-over editor and its stack (section 6.2): the root is `?doc=<id>` (a page's `open` replaces it), the
 * entries above it are pushed by `open` from inside the editor and live only here (a reload reopens the root).
 */
export function DocumentEditorHost({ children }: { children: ReactNode }) {
  const docParam = useUrlParam('doc');
  const tabParam = useUrlParam('tab');
  const workspace = useWorkspace();
  const catalog = useContent();
  useWorkspaceVersion(workspace);
  const rootId = docParam.value;
  const rootRef = useRef<Entry | null>(null);
  if (rootId === null) rootRef.current = null;
  else if (rootRef.current?.id !== rootId) {
    const m = workspace.mark();
    rootRef.current = { id: rootId, cancelMark: m, openMark: m };
  }
  const root = rootRef.current;
  const [nested, setNested] = useState<Entry[]>([]);
  const nestedRef = useRef(nested);
  nestedRef.current = nested;

  // a new root (or none: the back button, a page's open) drops the pushed entries; their pickers commit nothing
  useEffect(() => {
    const list = nestedRef.current;
    if (list.length === 0) return;
    setNested([]);
    for (const e of [...list].reverse()) e.resolve?.('cancelled');
  }, [rootId]);

  const openRoot = docParam.open;
  const stack = useMemo<EditorStackEntry[]>(() => (root ? [{ id: root.id }, ...nested.map(({ id, origin }) => ({ id, origin }))] : []), [root, nested]);
  // a page's open replaces the stack with [id]
  const outer = useMemo<EditorHost>(
    () => ({
      open: (id) => {
        openRoot(id, { tab: null });
        return Promise.resolve('confirmed');
      },
      stack,
    }),
    [openRoot, stack],
  );
  // an open from inside the editor pushes onto the stack and resolves when that entry pops
  const push = useCallback<EditorHost['open']>(
    (id, opts) =>
      new Promise<Outcome>((resolve) => {
        const list = nestedRef.current;
        const topId = list[list.length - 1]?.id ?? rootRef.current?.id;
        if (topId === undefined || topId === id) {
          if (topId === undefined) openRoot(id, { tab: null });
          resolve('confirmed');
          return;
        }
        const m = workspace.mark();
        const origin: EditorOrigin = { ...opts?.origin, parentId: opts?.origin?.parentId ?? topId };
        const entry: Entry = { id, origin, cancelMark: opts?.cancelMark ?? m, openMark: m, resolve };
        nestedRef.current = [...list, entry];
        setNested(nestedRef.current);
      }),
    [workspace, openRoot],
  );
  const inner = useMemo<EditorHost>(() => ({ open: push, stack }), [push, stack]);

  /** closes the top entry: the root leaves the URL, a pushed one pops and resolves its opener */
  const closeTop = (outcome: CloseOutcome) => {
    const list = nestedRef.current;
    if (list.length === 0) {
      if (outcome === 'cancelled' && rootRef.current) workspace.revertTo(rootRef.current.cancelMark);
      docParam.set({ doc: null, tab: null });
      return;
    }
    const top = list[list.length - 1]!;
    if (outcome === 'cancelled') workspace.revertTo(top.cancelMark);
    nestedRef.current = list.slice(0, -1);
    setNested(nestedRef.current);
    top.resolve?.(outcome === 'confirmed' ? 'confirmed' : 'cancelled');
  };
  /** a crumb click: pops to the entry at `index` of the whole stack (0 = the root), confirming the ones above it, top first */
  const popTo = (index: number) => {
    const list = nestedRef.current;
    const popped = list.slice(index).reverse();
    nestedRef.current = list.slice(0, index);
    setNested(nestedRef.current);
    for (const e of popped) e.resolve?.('confirmed');
  };
  const labelOf = (id: string) => {
    const d = (workspace.getDraft(entityKey('document', id))?.current as unknown as Document | undefined) ?? catalog.resolve(id);
    return d ? catalog.label(d) : isTempId(id) ? 'New document' : `${id.slice(0, 8)}…`;
  };

  const top: Entry | null = nested[nested.length - 1] ?? root;
  const all: Entry[] = root ? [root, ...nested] : [];
  const crumbs: EditorCrumb[] = all.slice(0, -1).map((e, i) => ({ label: labelOf(e.id), via: all[i + 1]?.origin?.label, onSelect: () => popTo(i) }));
  return (
    <EditorHostContext.Provider value={outer}>
      {children}
      {top && (
        <EditorHostContext.Provider value={inner}>
          {nested.length === 0 ? (
            <DocumentEditor key={top.id} id={top.id} openMark={top.openMark} tab={tabParam.value ?? 'fields'} onTab={(t) => tabParam.set({ tab: t === 'fields' ? null : t })} onClose={closeTop} />
          ) : (
            <DocumentEditor key={`${top.id}:${nested.length}`} id={top.id} nested openMark={top.openMark} crumbs={crumbs} onClose={closeTop} />
          )}
        </EditorHostContext.Provider>
      )}
    </EditorHostContext.Provider>
  );
}

export interface DocumentEditorProps {
  id: string;
  /** an entry pushed over the root: its tab is local, Done pops to the parent */
  nested?: boolean;
  /** the undo position when it opened: past it, Escape, the close button and the backdrop ask before they cancel */
  openMark?: number;
  /** the entries below this one, root first */
  crumbs?: EditorCrumb[];
  tab?: string;
  onTab?: (tab: string) => void;
  onClose: (outcome: CloseOutcome) => void;
}

/** Asks the shell to Save All through its own shortcut (Ctrl+S, ui/KeyboardShortcuts.tsx), the one path to useSaveAll. */
function requestSaveAll() {
  window.dispatchEvent(new KeyboardEvent('keydown', { key: 's', ctrlKey: true, bubbles: true, cancelable: true }));
}

export function DocumentEditor({ id, nested, openMark, crumbs, tab, onTab, onClose }: DocumentEditorProps) {
  const { catalog, workspace, client, org, game, gameId, branch, host } = useWidgetEnv();
  useWorkspaceVersion(workspace);
  const { can } = useGame();
  const toasts = useToasts();
  const confirm = useConfirm();
  const canWrite = can('content_write') && !workspace.isProtected;
  const draft = workspace.getDraft(entityKey('document', id));
  const fetched = useQuery(isTempId(id) ? null : `branch:${gameId}:${branch}:document:${id}`, () => client.data(ops.getDocument, { path: { org, game, branch, document: id } }), { head: workspace.head });
  const resolved = catalog.resolve(id) as Document | undefined;
  const server = fetched.data ?? (resolved && !isTempId(id) ? resolved : undefined);
  const current = (draft?.current as unknown as Document | undefined) ?? server ?? null;
  const [localTab, setLocalTab] = useState('fields');
  const activeTab = nested ? localTab : (tab ?? 'fields');
  const setTab = nested ? setLocalTab : (onTab ?? setLocalTab);
  const [dialog, setDialog] = useState<null | 'replace' | 'copy' | 'change'>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const createFor = useCreateFor();
  const bodyRef = useRef<HTMLDivElement>(null);

  const template = current ? catalog.templates.get(current.template) : undefined;
  const label = current ? catalog.label(current) : 'Document';
  /** Cancel (the button): reverts this entry's edits since its mark and closes it (section 4.4) */
  const cancel = () => onClose('cancelled');
  /** Escape, the close button, the backdrop: Cancel, asking first when the entry changed something since it opened */
  const requestCancel = async () => {
    if (openMark !== undefined && workspace.mark() > openMark) {
      const ok = await confirm({ title: `Discard the changes to ${label}?`, body: 'The edits made since this document opened are reverted.', confirmLabel: 'Discard', cancelLabel: 'Keep editing', tone: 'danger' });
      if (!ok) return;
    }
    cancel();
  };
  /** Done: keeps the edits (they stay staged until Save All) and closes, or pops to the parent */
  const done = () => {
    (document.activeElement as HTMLElement | null)?.blur?.(); // a field being typed in commits first
    onClose('confirmed');
  };
  const doneAndSave = () => {
    done();
    requestSaveAll();
  };
  const doneRef = useRef(done);
  doneRef.current = nested || !canWrite ? done : doneAndSave;
  // Ctrl+Enter: Done and save all on the root, Done on a pushed entry; only when the key comes from this drawer itself
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || !(e.ctrlKey || e.metaKey)) return;
      const drawer = bodyRef.current?.closest('[role="dialog"]');
      const from = e.target instanceof Element ? e.target.closest('[role="dialog"]') : null;
      if (!drawer || (from !== drawer && e.target !== document.body)) return;
      e.preventDefault();
      doneRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  const edit = (path: string, value: unknown) => {
    if (!current) return;
    workspace.edit('document', current.id, path, value, { base: server ? baseOf(server) : undefined, label: catalog.label(current), template: current.template });
  };
  const crumbTrail = crumbs && crumbs.length > 0 && (
    <nav className="editor-crumbs" aria-label="Open documents">
      {crumbs.map((c, i) => (
        <span key={i} className="editor-crumb">
          <button type="button" className="link" onClick={c.onSelect} title={`Back to ${c.label}`}>
            {c.label}
          </button>
          <span className="muted" aria-hidden="true">
            {' › '}
          </span>
          {c.via && (
            <>
              <span className="muted">{c.via}</span>
              <span className="muted" aria-hidden="true">
                {' › '}
              </span>
            </>
          )}
        </span>
      ))}
    </nav>
  );

  if (!current) {
    return (
      <Modal title={<span className="editor-title">{crumbTrail}<span>Document</span></span>} open onClose={() => onClose('cancelled')} drawer className="document-editor">
        {fetched.error ? (
          <p className="error">{fetched.error instanceof ApiError ? fetched.error.message : String(fetched.error)}</p>
        ) : isTempId(id) ? (
          <p className="muted">This draft no longer exists.</p>
        ) : (
          <p className="muted">Loading…</p>
        )}
      </Modal>
    );
  }
  const data = (current.data ?? {}) as Record<string, Json>;
  const readOnly = !canWrite || draft?.status === 'deleted';
  const groups = groupParams(template?.effectiveParameters ?? [], current.template, catalog.templates);
  const meta = (current.meta ?? {}) as Record<string, Json>;

  // section 6.1 Delete: staged at once (no dialog) and the editor closes; the toast's Undo restores the draft only
  const remove = async () => {
    stageDeletion(workspace, toasts, [{ id: current.id, label, template: current.template, base: server ? baseOf(server) : undefined }]);
    onClose('deleted'); // a pushed entry pops without committing a reference to a deleted document
  };
  const clone = () => {
    const copy = cloneDocument(workspace, catalog, current);
    toasts.success(`Copy staged as a new ${template?.displayName ?? current.template.split('.').pop()}.`);
    void host?.open(copy);
  };
  const actions = [
    { key: 'clone', label: 'Clone', onSelect: clone, disabled: !canWrite },
    { key: 'export', label: current.export ? 'Export: on → off' : 'Export: off → on', onSelect: () => edit('export', !current.export), disabled: readOnly },
    { key: 'replace', label: 'Replace by…', onSelect: () => setDialog('replace'), disabled: !canWrite || isTempId(current.id) },
    { key: 'copy', label: 'Copy to branch…', onSelect: () => setDialog('copy'), disabled: isTempId(current.id) },
    { key: 'change', label: 'Change template…', onSelect: () => setDialog('change'), disabled: !canWrite || isTempId(current.id) },
    { key: 's1', label: '', separator: true },
    { key: 'override', label: 'Create override…', onSelect: () => createFor.open('override', current, () => onClose('confirmed')), disabled: !canWrite },
    { key: 'abtest', label: 'Create A/B test…', onSelect: () => createFor.open('abtest', current, () => onClose('confirmed')), disabled: !canWrite },
    { key: 's2', label: '', separator: true },
    { key: 'delete', label: 'Delete', onSelect: () => void remove(), danger: true, disabled: readOnly },
  ];

  return (
    <Modal
      title={
        <span className="row wrap editor-title">
          {crumbTrail}
          <span>{label}</span>
          <Link to={routes.template(org, game, branch, current.template)} className="muted small" title="Open the template">
            <code>{current.template}</code>
          </Link>
          {draft && <Badge tone={draftTone[draft.status]}>{draftWord[draft.status]}</Badge>}
          {!current.export && <Badge tone="warning">HDN</Badge>}
        </span>
      }
      open
      onClose={() => void requestCancel()}
      drawer
      className="document-editor"
      footer={
        <>
          {!readOnly && (
            <Button variant="danger" onClick={() => void remove()}>
              Delete
            </Button>
          )}
          <span className="spacer" />
          <Button onClick={cancel}>Cancel</Button>
          {nested || !canWrite ? (
            <Button variant="primary" onClick={done} title={nested ? 'Back to the document below, with this one picked (Ctrl+Enter)' : undefined}>
              Done
            </Button>
          ) : (
            <>
              <Button onClick={done}>Done</Button>
              <Button variant="primary" onClick={doneAndSave} title="Done, then Save All (Ctrl+Enter)">
                Done and save all
              </Button>
            </>
          )}
        </>
      }
    >
      <div ref={bodyRef} hidden />
      <Tabs
        segmented
        ariaLabel="Document editor"
        value={activeTab}
        onChange={setTab}
        items={[
          { key: 'fields', label: 'Fields' },
          { key: 'references', label: 'References' },
          { key: 'history', label: 'History' },
          { key: 'json', label: 'JSON' },
        ]}
      />
      {activeTab === 'fields' && (
        <div className="editor-grid">
          <div>
            {groups.map((g) => (
              <details key={g.name} className="field-group" open>
                <summary>
                  {g.own ? 'Fields' : `Inherited from ${g.name}`} <span className="muted small">({g.params.length})</span>
                </summary>
                {g.params.map((p) => (
                  <Field key={p.name} label={p.displayName ?? p.name} help={parameterHelp(p, current, catalog.templates)} required={p.isRequired}>
                    <FieldWidget id={`f-${p.name}`} param={p} value={data[p.name]} disabled={readOnly} owner={current.id} onCommit={(v) => edit(`data.${p.name}`, v)} onCommitAt={(rel, v) => edit(joinPath(`data.${p.name}`, rel), v)} />
                  </Field>
                ))}
              </details>
            ))}
            {groups.length === 0 && <p className="muted">This template has no parameters.</p>}
          </div>
          <div className="stack editor-side">
            <dl className="kv small">
              <dt>Id</dt>
              <dd>
                <code>{isTempId(current.id) ? 'assigned on save' : current.id}</code>
              </dd>
              <dt>Legacy id</dt>
              <dd>{current.unnyId ?? '—'}</dd>
              <dt>Version</dt>
              <dd>{isTempId(current.id) ? '—' : String(current.version)}</dd>
            </dl>
            <Switch label="Exported (part of releases)" checked={current.export} disabled={readOnly} onChange={(v) => edit('export', v)} />
            <Field label="Tags">
              <TagInput id="doc-tags" value={current.tags} suggestions={catalog.tags.map((t) => t.name)} disabled={readOnly} onChange={(tags) => edit('tags', tags)} />
            </Field>
            <Field label="Notes" help="Editor-only; never released">
              <CommitTextInput id="doc-notes" multiline value={typeof meta.notes === 'string' ? meta.notes : ''} disabled={readOnly} onCommit={(v) => edit('meta', v ? { ...meta, notes: v } : omit(meta, 'notes'))} aria-label="Notes" />
            </Field>
            <div className="row">
              <Button size="s" onClick={(e) => setMenuAnchor(e.currentTarget)} aria-haspopup="menu">
                Actions <ChevronDown aria-hidden="true" />
              </Button>
              <Menu anchor={menuAnchor} open={menuAnchor !== null} onClose={() => setMenuAnchor(null)} label="Document actions" items={actions} />
            </div>
            {draft && draft.changes.length > 0 && (
              <div className="card small">
                <strong>
                  {draft.changes.length} staged change{draft.changes.length === 1 ? '' : 's'}
                </strong>
                <ul className="change-list">
                  {draft.changes.map((c) => (
                    <li key={c.path} className="row">
                      <code>{c.path.replace(/^data\./, '')}</code>
                      <span className="spacer" />
                      {!readOnly && (
                        <IconButton label={`Discard the change to ${c.path}`} onClick={() => workspace.discardChange(draft.key, c.path)}>
                          <Undo2 aria-hidden="true" />
                        </IconButton>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {draft?.error && (
              <p className="error small" role="alert">
                {draft.error.message}
              </p>
            )}
          </div>
        </div>
      )}
      {activeTab === 'references' && <ReferencesTab id={current.id} />}
      {activeTab === 'history' && <HistoryTab id={current.id} />}
      {activeTab === 'json' && <JsonTab doc={current} />}
      {dialog === 'replace' && <ReplaceByDialog doc={current} onClose={() => setDialog(null)} />}
      {dialog === 'copy' && <CopyToBranchDialog docs={[server ?? current]} onClose={() => setDialog(null)} />}
      {dialog === 'change' && <ChangeTemplateDialog docs={[server ?? current]} onClose={() => setDialog(null)} onDone={() => onClose('confirmed')} />}
      {createFor.dialog}
    </Modal>
  );
}

function omit(o: Record<string, Json>, key: string): Json {
  const out: Record<string, Json> = {};
  for (const [k, v] of Object.entries(o)) if (k !== key) out[k] = v;
  return Object.keys(out).length === 0 ? null : out;
}

/** The parameters by declaring template: the own ones first, then each base in ancestry order. */
export function groupParams(params: Parameter[], template: string, templates: Map<string, { parent?: string | null }>): { name: string; own: boolean; params: Parameter[] }[] {
  const order: string[] = [template];
  let p = templates.get(template)?.parent;
  const seen = new Set<string>([template]);
  while (p && !seen.has(p)) {
    order.push(p);
    seen.add(p);
    p = templates.get(p)?.parent;
  }
  const byName = new Map<string, Parameter[]>();
  for (const param of params) {
    const owner = param.declaredBy && param.declaredBy !== template && order.includes(param.declaredBy) ? param.declaredBy : template;
    const list = byName.get(owner) ?? [];
    list.push(param);
    byName.set(owner, list);
  }
  return order.filter((n) => byName.has(n)).map((n) => ({ name: n, own: n === template, params: byName.get(n)! }));
}

function ReferencesTab({ id }: { id: string }) {
  const { catalog, client, org, game, gameId, branch, workspace, host } = useWidgetEnv();
  const q = useQuery(isTempId(id) ? null : `branch:${gameId}:${branch}:references:${id}`, () => client.all<DocumentReference>((cursor) => client.data(ops.listDocumentReferences, { path: { org, game, branch, document: id }, query: { limit: 200, cursor } })), { head: workspace.head });
  const refs = q.data ?? [];
  useEffect(() => {
    void catalog.resolver.load(refs.map((r) => r.documentId));
  }, [refs, catalog.resolver]);
  if (isTempId(id)) return <p className="muted">A new document has no references yet.</p>;
  if (q.loading && !q.data) return <p className="muted">Loading…</p>;
  if (q.error) return <p className="error">{q.error instanceof ApiError ? q.error.message : String(q.error)}</p>;
  if (refs.length === 0) return <p className="muted">This document is not referenced anywhere.</p>;
  const documents = new Set(refs.map((r) => r.documentId)).size;
  return (
    <div className="stack">
      <p>
        <strong>
          {refs.length} reference{refs.length === 1 ? '' : 's'} in {documents} document{documents === 1 ? '' : 's'}
        </strong>
      </p>
      <ul className="ref-list">
        {refs.map((r, i) => {
          const d = catalog.resolve(r.documentId);
          return (
            <li key={`${r.documentId}-${r.path}-${i}`} className="row">
              <button type="button" className="link" onClick={() => void host?.open(r.documentId)}>
                {d ? catalog.label(d) : r.documentId.slice(0, 8)}
              </button>
              <Badge>{r.template.split('.').pop()}</Badge>
              <code className="muted small">› {r.path}</code>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function HistoryTab({ id }: { id: string }) {
  const { client, org, game, gameId, branch, workspace } = useWidgetEnv();
  const { offsets } = useGame();
  const [zone] = useTimeZone();
  const q = useQuery(isTempId(id) ? null : `branch:${gameId}:${branch}:history:${id}`, () => client.data(ops.listDocumentHistory, { path: { org, game, branch, document: id }, query: { limit: 50 } }), { head: workspace.head });
  if (isTempId(id)) return <p className="muted">A new document has no history yet.</p>;
  if (q.loading && !q.data) return <p className="muted">Loading…</p>;
  if (q.error) return <p className="error">{q.error instanceof ApiError ? q.error.message : String(q.error)}</p>;
  const commits = (q.data?.items ?? []) as Commit[];
  return (
    <div className="stack">
      <ul className="history-list">
        {commits.map((c) => (
          <li key={c.sha} className="history-row">
            <code className="muted">{c.sha.slice(0, 8)}</code>
            <span>{c.authorName}</span>
            <span className="muted small">{c.authoredAt ? formatInstant(Math.floor(Date.parse(c.authoredAt) / 1000), zone, { offsets }) : ''}</span>
            <span className="history-message">{c.message}</span>
            <Link to={routes.history(org, game, branch, c.sha)} className="small" aria-label={`Open commit ${c.sha.slice(0, 8)} in History`}>
              Open in History
            </Link>
          </li>
        ))}
        {commits.length === 0 && <li className="muted">No commit touches this document yet.</li>}
      </ul>
    </div>
  );
}

function JsonTab({ doc }: { doc: Document }) {
  const toasts = useToasts();
  const text = JSON.stringify(doc, null, 2);
  return (
    <div className="stack">
      <div className="row">
        <span className="muted small">
          {shortId(doc)} · {doc.template}
        </span>
        <span className="spacer" />
        <Button size="s" onClick={() => void navigator.clipboard?.writeText(text).then(() => toasts.info('Copied.'))}>
          Copy
        </Button>
      </div>
      <pre className="json">{text}</pre>
    </div>
  );
}
